import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BulkActionResultModel, BulkMoveDocumentsRequest, DocumentNodeModel } from '../../../../core/models/data/document.model';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { DocumentListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ModalActions } from '../../../../core/enums/base.enums';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { FolderTreePickerComponent, FolderTreeSelection } from '../../../../parts/folder-tree-picker/folder-tree-picker.component';

/**
 * Destination picker for bulk-moving selected files into a folder. Unlike the single-folder move
 * modal, files always live inside a folder (no Root destination) and there is no circular-move
 * concern, so every folder in the tree is a valid destination. The moved files adopt the
 * destination root's permission (handled server-side); the API auto-suffixes on a name collision.
 */
@Component({
    selector: 'ms-document-bulk-move-files-modal',
    standalone: true,
    imports: [FontAwesomeModule, EscCloseDirective, FolderTreePickerComponent],
    templateUrl: './document-bulk-move-files-modal.component.html'
})
export class DocumentBulkMoveFilesModalComponent {

    public fileNodes = input<DocumentNodeModel[]>([]);
    public folderTree = input<DocumentNodeModel[]>([]);

    public submitted = output<BulkActionResultModel>();
    public cancelled = output<void>();

    public modalId = DocumentsConstants.BULK_MOVE_FILES_MODAL_ID();

    public selectedDestination = signal<FolderTreeSelection>(undefined);
    public isSubmitting = signal<boolean>(false);
    public errorMessage = signal<string>('');

    private documentService = inject(DocumentService);
    private bootstrapService = inject(BootstrapService);

    public fileCount = computed<number>(() => this.fileNodes().length);

    public canSubmit = computed<boolean>(() => this.selectedDestination() !== undefined && !this.isSubmitting());

    // Fetch a folder's child folders the first time the picker expands it (lazy — no full-tree crawl).
    public loadChildren = (folderId: number): Promise<DocumentNodeModel[]> =>
        this.documentService.getChildren(folderId, DocumentListSortOptions.NameAsc)
            .then(result => result.success ? (result.data ?? []) : []);

    constructor() {
        // Reset selection / error whenever the file selection changes (new modal open). The picker
        // collapses its tree when the selection clears.
        effect(() => {
            const _ = this.fileNodes();
            this.selectedDestination.set(undefined);
            this.errorMessage.set('');
        });
    }

    public async submit(): Promise<void> {
        const destination = this.selectedDestination();
        const files = this.fileNodes();
        // The picker never offers Root here (showRoot is false), so a selection is always a folder id.
        if (typeof destination !== 'number' || files.length === 0) return;

        this.isSubmitting.set(true);
        this.errorMessage.set('');

        const request: BulkMoveDocumentsRequest = {
            useFilter: false,
            selectedIds: files.map(f => f.documentNodeId),
            excludedIds: null,
            filter: null,
            note: null,
            destinationFolderId: destination
        };
        const result = await this.documentService.bulkMoveDocuments(request);
        this.isSubmitting.set(false);

        if (result.success && result.data) {
            this.submitted.emit(result.data);
            this.close();
            return;
        }

        this.errorMessage.set(result.message ?? DocumentsMessagingConstants.DOCUMENTS_BULK_MOVE_FAILED());
    }

    public cancel(): void {
        this.cancelled.emit();
        this.close();
    }

    public close(): void {
        this.bootstrapService.manageModal(`#${this.modalId}`, ModalActions.Hide);
    }

}
